import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { clampToViewport as clampToViewportWithin } from '../../lib/chatColumnAnchor';

const EDGE_MARGIN = 12;

const clampToViewport = (position, size) => clampToViewportWithin(position, size, { margin: EDGE_MARGIN });

// Re-clamping runs on every resize pointer event, and a fresh object each time would commit a
// second render per event even when nothing moved — which is the common case, since resizing
// only changes the width unless the window is up against an edge.
const withSamePosition = (current, next) => (current && current.left === next.left && current.top === next.top ? current : next);

// A mouse keeps the same pointerId for the life of the page, so a gesture we never closed
// out stays a live match for every later hover — the window then drags or resizes with no
// button held. Every way a gesture can end has to clear it: pointerup, pointercancel, and
// lostpointercapture (fired when the browser or a re-render takes the capture away).
const isPointerHeld = (event) => event.buttons !== 0;

const capturePointer = (event) => {
  try {
    event.currentTarget.setPointerCapture(event.pointerId);
  } catch {
    // Capture is an optimisation, not a requirement — the buttons check still ends the
    // gesture correctly without it.
  }
};

const releasePointer = (event) => {
  try {
    if (event.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  } catch {
    // Already released, or the element is gone. Nothing left to do.
  }
};

// The window remounts as you move between chats — the room outlives it, React state does
// not — so what the user set up would be thrown away on every navigation. Size and position
// live out here instead, tagged with the call they belong to. Same call, any chat: restored.
// Next call: the tag no longer matches and it opens fresh, which is what we want, since a
// position is a decision about one call and not a preference to carry forward.
let storedWindowState = null;

const readStoredWindowState = (callKey) => (callKey != null && storedWindowState?.callKey === callKey ? storedWindowState : null);

// Called when a call ends. The callKey alone isn't enough to start fresh: rejoining the same
// chat produces the same key, so the old size and position would come back. Clearing on the
// way out — rather than on the way in — keeps a mid-call remount restoring what the user set
// up, while every new session opens at the default.
export function resetStoredWindowState() {
  storedWindowState = null;
}

// Pointer-drag for a viewport-fixed element, clamped so it can never be parked
// off-screen. Only the returned handle props start a drag, so the window moves by its
// title bar rather than from anywhere on the video.
//
// It opens centred across the chat column with its top level with the channels card, and
// keeps re-placing itself — as the viewport changes, as the sidebar resizes, as faces join
// and change its height — until the user takes hold of it. Dragging or resizing pins it for
// the rest of the call.
export function useDraggableWindowPosition({ callKey, topAnchor, topOffset = 0, width: widthRange }) {
  const nodeRef = useRef(null);
  const [restored] = useState(() => readStoredWindowState(callKey));
  const [position, setPosition] = useState(restored?.position ?? null);
  const [width, setWidth] = useState(restored?.width ?? widthRange.initial);
  const [isPinned, setIsPinned] = useState(restored?.isPinned ?? false);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  // Write through on every change so a remount mid-call picks up exactly where it left off.
  useEffect(() => {
    storedWindowState = { callKey, position, width, isPinned };
  }, [callKey, position, width, isPinned]);

  // Centred across the column, but sitting on the same line as the channels card rather
  // than centred vertically. Both axes come from the column's own box: this window is fixed
  // to the viewport while the card's offset is stated inside the column, and the two frames
  // only agree if we read the real one. Deriving left from a passed-in rail width instead
  // drifts the moment anything else narrows the column — the widgets drawer does exactly
  // that, and the window would slide out over the drawer while the card stayed put.
  const placeInColumn = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const size = { width: rect.width, height: rect.height };
    const column = (topAnchor ? document.querySelector(topAnchor) : null)?.getBoundingClientRect();
    const next = clampToViewport(
      {
        left: column ? column.left + (column.width - size.width) / 2 : (window.innerWidth - size.width) / 2,
        top: (column ? column.top : 0) + topOffset,
      },
      size,
    );
    setPosition((current) => withSamePosition(current, next));
  }, [topAnchor, topOffset]);

  // Centre on mount, and again whenever the column or the dragged width shifts underneath.
  useLayoutEffect(() => {
    if (isPinned) return;
    placeInColumn();
  }, [placeInColumn, isPinned, width]);

  // Two things move the window without any state of ours changing: the face grid resizing it
  // as people join or leave, and the column itself narrowing under it when the widgets drawer
  // opens or the rail is dragged. Watch both.
  useEffect(() => {
    if (isPinned) return;
    const node = nodeRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => placeInColumn());
    observer.observe(node);
    const column = topAnchor ? document.querySelector(topAnchor) : null;
    if (column) observer.observe(column);
    return () => observer.disconnect();
  }, [placeInColumn, isPinned, topAnchor]);

  // Unpinned it re-centres; pinned it only gets pulled back from an edge it would fall off.
  // Either way the width comes down with the viewport, so the call window is never wider than
  // the browser window it sits in. Clamping position can't do this on its own — that only
  // moves the origin, leaving an oversized window hanging off the edge.
  useEffect(() => {
    const handleResize = () => {
      const node = nodeRef.current;
      if (!node) return;

      setWidth((current) => Math.max(widthRange.min, Math.min(current, window.innerWidth - EDGE_MARGIN * 2)));

      if (!isPinned) {
        placeInColumn();
        return;
      }
      const rect = node.getBoundingClientRect();
      setPosition((current) =>
        current ? withSamePosition(current, clampToViewport(current, { width: rect.width, height: rect.height })) : current,
      );
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [placeInColumn, isPinned, widthRange.min]);

  const handlePointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const node = nodeRef.current;
    if (!node) return;
    // Buttons inside the title bar keep working normally.
    if (event.target instanceof Element && event.target.closest('button, a, input, select, textarea')) return;

    const rect = node.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      size: { width: rect.width, height: rect.height },
    };
    capturePointer(event);
    event.preventDefault();
  }, []);

  const endDrag = useCallback((event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    releasePointer(event);
  }, []);

  // Pinning happens on the first actual movement, not on pointer-down — a stray click on
  // the title bar shouldn't quietly opt the window out of centring.
  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (!isPointerHeld(event)) {
        endDrag(event);
        return;
      }
      setIsPinned(true);
      setPosition(clampToViewport({ left: event.clientX - drag.offsetX, top: event.clientY - drag.offsetY }, drag.size));
    },
    [endDrag],
  );

  // Resize from either side edge. Width drives everything — the grid of faces reflows to it
  // — so height is never set directly.
  const handleResizePointerDown = useCallback(
    (edge) => (event) => {
      if (event.button !== 0) return;
      const node = nodeRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      resizeRef.current = { pointerId: event.pointerId, edge, startX: event.clientX, startWidth: rect.width, startLeft: rect.left };
      capturePointer(event);
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const endResize = useCallback((event) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return;
    resizeRef.current = null;
    releasePointer(event);
  }, []);

  // Also pins: a window that re-centred mid-drag would slide out from under the pointer.
  const handleResizePointerMove = useCallback(
    (event) => {
      const resize = resizeRef.current;
      if (!resize || resize.pointerId !== event.pointerId) return;
      if (!isPointerHeld(event)) {
        endResize(event);
        return;
      }
      setIsPinned(true);
      const delta = event.clientX - resize.startX;
      // Growing stops at the edge of the browser window rather than overshooting it and being
      // snapped back. One side of the window is anchored during a resize, so the room to grow
      // is the gap between that anchored side and the far edge of the viewport.
      const anchoredRight = resize.startLeft + resize.startWidth;
      const roomToGrow = resize.edge === 'left' ? anchoredRight - EDGE_MARGIN : window.innerWidth - resize.startLeft - EDGE_MARGIN;
      const maxWidth = Math.max(widthRange.min, Math.min(widthRange.max, roomToGrow));
      const next = Math.min(
        Math.max(resize.edge === 'left' ? resize.startWidth - delta : resize.startWidth + delta, widthRange.min),
        maxWidth,
      );
      setWidth(next);
      // Dragging the left edge grows the window leftwards, so the origin moves with it and
      // the right edge stays put.
      if (resize.edge === 'left') {
        setPosition((current) => (current ? { ...current, left: resize.startLeft + (resize.startWidth - next) } : current));
      }
    },
    [endResize, widthRange.min, widthRange.max],
  );

  // Pinned, growing near an edge would otherwise push the window off-screen.
  useLayoutEffect(() => {
    if (!isPinned) return;
    const node = nodeRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setPosition((current) =>
      current ? withSamePosition(current, clampToViewport(current, { width: rect.width, height: rect.height })) : current,
    );
  }, [isPinned, width]);

  const resizeHandleProps = useCallback(
    (edge) => ({
      onPointerDown: handleResizePointerDown(edge),
      onPointerMove: handleResizePointerMove,
      onPointerUp: endResize,
      onPointerCancel: endResize,
      onLostPointerCapture: endResize,
    }),
    [handleResizePointerDown, handleResizePointerMove, endResize],
  );

  return {
    nodeRef,
    position,
    width,
    dragHandleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onLostPointerCapture: endDrag,
    },
    resizeHandleProps,
  };
}
